import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

interface ShaderProps {
  isGenerating?: boolean;
  isCursed?: boolean;
  dimmingDisabled?: boolean;
}

const InteractiveStarfieldShader: React.FC<ShaderProps> = ({ 
  isGenerating = false, 
  isCursed = false, 
  dimmingDisabled = false 
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // Fix: Added initial value null to useRef to satisfy TypeScript requirements
  const materialRef = useRef<THREE.ShaderMaterial | null>(null);

  // Update shader uniforms when props change
  useEffect(() => {
    if (materialRef.current) {
      materialRef.current.uniforms.hasActiveReminders.value = isGenerating;
    }
  }, [isGenerating]);

  useEffect(() => {
    if (materialRef.current) {
      materialRef.current.uniforms.hasUpcomingReminders.value = isCursed;
    }
  }, [isCursed]);

  useEffect(() => {
    if (materialRef.current) {
      materialRef.current.uniforms.disableCenterDimming.value = dimmingDisabled;
    }
  }, [dimmingDisabled]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
      renderer.setPixelRatio(window.devicePixelRatio);
      container.appendChild(renderer.domElement);
    } catch (err) {
      console.error('WebGL not supported', err);
      container.innerHTML = '<p style="color:white;text-align:center;">Sorry, WebGL isn’t available.</p>';
      return;
    }

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const clock = new THREE.Clock();

    const vertexShader = `
      varying vec2 vTextureCoord;
      void main() {
        vTextureCoord = uv;
        gl_Position = vec4(position, 1.0);
      }
    `;

    const fragmentShader = `
      precision mediump float;
      uniform vec2 iResolution;
      uniform float iTime;
      uniform bool hasActiveReminders;
      uniform bool hasUpcomingReminders;
      uniform bool disableCenterDimming;
      varying vec2 vTextureCoord;

      void mainImage(out vec4 O, in vec2 fragCoord) {
        O = vec4(0.0, 0.0, 0.0, 1.0);
        vec2 b = vec2(0.0, 0.2);
        vec2 p;
        mat2 R = mat2(1.0, 0.0, 0.0, 1.0);
        
        vec2 center = iResolution.xy * 0.5;
        float dist = distance(fragCoord, center);
        float radius = min(iResolution.x, iResolution.y) * 0.5;
        
        float centerDim = disableCenterDimming ? 1.0 : smoothstep(radius * 0.3, radius * 0.5, dist);
        
        // Speed multiplier when generating
        float timeScale = hasActiveReminders ? iTime * 2.5 : iTime;

        for(int i = 0; i < 20; i++) {
          float fi = float(i) + 1.0;
          float angle = fi + 0.0;
          float c = cos(angle);
          float s = sin(angle);
          R = mat2(c, -s, s, c);
          
          float angle2 = fi + 33.0;
          float c2 = cos(angle2);
          float s2 = sin(angle2);
          mat2 R2 = mat2(c2, -s2, s2, c2);
          
          vec2 coord = fragCoord / iResolution.y * fi * 0.1 + timeScale * b;
          vec2 frac_coord = fract(coord * R2) - 0.5;
          p = R * frac_coord;
          vec2 clamped_p = clamp(p, -b, b);
          
          float len = length(clamped_p - p);
          if (len > 0.0) {
            vec4 star = 1e-3 / len * (cos(p.y / 0.1 + vec4(0.0, 1.0, 2.0, 3.0)) + 1.0);
            O += star;
          }
        }
        
        if (hasActiveReminders) {
          O.rgb = mix(O.rgb, vec3(0.5, 0.2, 1.0), 0.5); // Purple aura when cooking
        } else if (hasUpcomingReminders) {
          O.rgb = mix(O.rgb, vec3(1.0, 0.0, 0.0), 0.4); // Red tint for CURSED
        }
        
        if (!disableCenterDimming) {
          O.rgb = mix(O.rgb * 0.2, O.rgb, centerDim);
        }
      }

      void main() {
        vec4 color;
        mainImage(color, vTextureCoord * iResolution);
        gl_FragColor = color;
      }
    `;

    const uniforms = {
      iTime: { value: 0 },
      iResolution: { value: new THREE.Vector2() },
      hasActiveReminders: { value: isGenerating },
      hasUpcomingReminders: { value: isCursed },
      disableCenterDimming: { value: dimmingDisabled }
    };
    const material = new THREE.ShaderMaterial({ vertexShader, fragmentShader, uniforms });
    materialRef.current = material;
    const geometry = new THREE.PlaneGeometry(2, 2);
    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    const onResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      renderer.setSize(w, h);
      uniforms.iResolution.value.set(w, h);
    };

    window.addEventListener('resize', onResize);
    onResize();

    renderer.setAnimationLoop(() => {
      uniforms.iTime.value = clock.getElapsedTime();
      renderer.render(scene, camera);
    });

    return () => {
      window.removeEventListener('resize', onResize);
      renderer.setAnimationLoop(null);
      if (renderer.domElement && renderer.domElement.parentNode) {
        renderer.domElement.parentNode.removeChild(renderer.domElement);
      }
      material.dispose();
      geometry.dispose();
      renderer.dispose();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: -1,
        backgroundColor: '#000',
        pointerEvents: 'none'
      }}
      aria-hidden="true"
    />
  );
};

export default InteractiveStarfieldShader;